import type { ProfileAssignment } from "./tunnelProfileShapes";

/** Color ramp direction for continuous columns */
export type ColorRamp = "green-red" | "red-green" | "blue-red" | "red-blue";

/** Column definition for a borehole data table */
export interface DataTableColumn {
  name: string;
  type: "continuous" | "categorical";
  unit?: string;
  /** Color ramp for continuous columns (default: "green-red" = low is good) */
  colorRamp?: ColorRamp;
  /** For categorical columns: known categories with assigned colors */
  categories?: { value: string; color: string; label?: string }[];
}

/** Schema for a borehole data table (per-project) */
export interface DataTableSchema {
  id: string;
  name: string;
  /** API endpoint pattern. Use {scan} as placeholder for salvo scan number */
  endpointPattern: string;
  /** Columns available in this table (besides depth_from, depth_to which are always required) */
  columns: DataTableColumn[];
}

/** Per-salvo data table availability status */
export type DataTableStatus = "available" | "not_applicable" | "loading" | "error" | "empty";

/** Analysis that can be run on a point cloud scan */
export interface PointcloudAnalysis {
  id: string;
  label: string;
  /** Whether results have been computed and are available */
  hasResults: boolean;
  /** Index into pointcloudEndpoints for the result point cloud (hidden entry) */
  resultPointcloudIndex?: number;
  /** URL for a results image (e.g. stereonet plot) */
  resultImage?: string;
}

export interface ProjectConfig {
  id: string;
  name: string;
  /** API endpoint for the LandXML centerline */
  centerlineEndpoint: string;
  /** API endpoint(s) for point cloud data. Can be JSON or PLY. */
  pointcloudEndpoints: { url: string; format: "json" | "ply"; label: string; analyses?: PointcloudAnalysis[]; hidden?: boolean }[];
  /** Centerline endpoint used to compute UTM centering offsets for point clouds */
  pointcloudCenterlineEndpoint: string;
  /** Profile assignments along the tunnel */
  profileAssignments: ProfileAssignment[];
  /** Optional: API endpoint for MWD rock type data */
  mwdEndpoint?: string;
  /** Optional: API endpoint for salvo (drill-and-blast) hole data */
  salvoEndpoint?: string;
  /**
   * Optional UTM offset applied to point cloud coordinates before centering.
   * Use when point cloud files have absolute coords that differ from the
   * centerline (e.g. after relocating a demo centerline to Norway).
   * [deltaEasting, deltaNorthing, deltaElevation]
   */
  pointcloudUtmOffset?: [number, number, number];
  /** Data summary rows shown on the dashboard */
  dataSummary: { label: string; value: string; available: boolean }[];
  /** Borehole data table schemas for this project */
  dataTableSchemas?: DataTableSchema[];
}

export const PROJECTS: Record<string, ProjectConfig> = {
  "e16-fas": {
    id: "e16-fas",
    name: "E16 FAS",
    centerlineEndpoint: "/api/tunnel-centerline",
    pointcloudEndpoints: [
      { url: "/api/pointcloud/scan-2025-03-15", format: "json", label: "Scan 2025-03-15" },
    ],
    pointcloudCenterlineEndpoint: "/api/tunnel-centerline",
    profileAssignments: [
      { fromStation: 0, toStation: 200, profileType: "2-lane" },
      { fromStation: 200, toStation: 400, profileType: "3-lane" },
      { fromStation: 400, toStation: 9999, profileType: "2-lane" },
    ],
    salvoEndpoint: "/api/e16-fas-salvos",
    dataSummary: [
      { label: "Tunnel centerline", value: "1 alignment", available: true },
      { label: "Cross sections", value: "Imported", available: true },
      { label: "Pointcloud scans", value: "Available", available: true },
      { label: "Salvo / blast holes", value: "Available", available: true },
      { label: "Injection data", value: "Available", available: true },
      { label: "MWD data", value: "Not imported", available: false },
      { label: "GIS observations", value: "Field data", available: true },
      { label: "Photos", value: "Geotagged", available: true },
      { label: "Borehole logs", value: "Not imported", available: false },
      { label: "Unwrapped profile", value: "Available", available: true },
      { label: "Geological report", value: "Not started", available: false },
      { label: "Data export", value: "Not started", available: false },
    ],
  },
  "scan-tunnel": {
    id: "scan-tunnel",
    name: "Scan Tunnel",
    centerlineEndpoint: "/api/scan-tunnel-centerline",
    pointcloudEndpoints: [
      { url: "/api/scan/scan-10.ply", format: "ply", label: "Scan pf 160m", analyses: [{ id: "scan10-dips", label: "Dip Analysis", hasResults: true, resultPointcloudIndex: 9, resultImage: "/api/scan/dips_160.png" }] },
      { url: "/api/scan/scan-1.ply", format: "ply", label: "Scan 1" },
      { url: "/api/scan/scan-2.ply", format: "ply", label: "Scan 2" },
      { url: "/api/scan/scan-3.ply", format: "ply", label: "Scan 3" },
      { url: "/api/scan/scan-4.ply", format: "ply", label: "Scan 4" },
      { url: "/api/scan/scan-5.ply", format: "ply", label: "Scan 5" },
      { url: "/api/scan/scan-6.ply", format: "ply", label: "Scan 6" },
      { url: "/api/scan/scan-7.ply", format: "ply", label: "Scan 7" },
      { url: "/api/scan/scan-8.ply", format: "ply", label: "Scan 8" },
      { url: "/api/scan/96923_sprekker.ply", format: "ply", label: "Sprekker (Dip Analysis)", hidden: true },
    ],
    pointcloudCenterlineEndpoint: "/api/scan-tunnel-centerline",
    // Coordinates are native NTM Zone 10 (EPSG:5110) — no offset needed
    profileAssignments: [
      { fromStation: 0, toStation: 88, profileType: "large" },
      { fromStation: 88, toStation: 9999, profileType: "large", offset: [-0.5, -0.5] },
    ],
    salvoEndpoint: "/api/scan-tunnel-salvos",
    dataSummary: [
      { label: "Tunnel centerline", value: "1 alignment", available: true },
      { label: "Pointcloud scans", value: "10 scans", available: true },
      { label: "Salvo / blast holes", value: "8 rounds", available: true },
      { label: "MWD ML Interpretation", value: "Q-class + Rock type", available: true },
      { label: "Cross sections", value: "Not imported", available: false },
      { label: "GIS observations", value: "Not started", available: false },
      { label: "Photos", value: "Not imported", available: false },
    ],
    dataTableSchemas: [
      {
        id: "mwd_raw",
        name: "MWD Raw Sensors",
        endpointPattern: "/api/mwd-raw/scan-{scan}",
        columns: [
          { name: "PenetrNorm", type: "continuous", unit: "m/min", colorRamp: "red-green" },
          { name: "PenetrRMS", type: "continuous", colorRamp: "green-red" },
          { name: "RotaPressNorm", type: "continuous", unit: "bar", colorRamp: "green-red" },
          { name: "RotaPressRMS", type: "continuous", colorRamp: "green-red" },
          { name: "FeedPressNorm", type: "continuous", unit: "bar", colorRamp: "green-red" },
          { name: "HammerPressNorm", type: "continuous", unit: "bar", colorRamp: "green-red" },
          { name: "WaterFlowNorm", type: "continuous", unit: "l/min", colorRamp: "red-green" },
          { name: "WaterFlowRMS", type: "continuous", colorRamp: "green-red" },
        ],
      },
      {
        id: "mwd_ml",
        name: "MWD ML Interpretation",
        endpointPattern: "/api/mwd-ml/scan-{scan}",
        columns: [
          {
            name: "q_class",
            type: "categorical",
            categories: [
              { value: "very_good", color: "#00aa33", label: "Very good (Q > 10)" },
              { value: "good", color: "#44cc44", label: "Good (Q 4-10)" },
              { value: "fair", color: "#cccc00", label: "Fair (Q 1-4)" },
              { value: "poor", color: "#ee8800", label: "Poor (Q 0.1-1)" },
              { value: "very_poor", color: "#ee4400", label: "Very poor (Q < 0.1)" },
              { value: "weakness_zone", color: "#dd0000", label: "Weakness zone" },
            ],
          },
          {
            name: "rock_type",
            type: "categorical",
            categories: [
              { value: "Granite", color: "#cc6666", label: "Granite" },
              { value: "Gneiss", color: "#6688cc", label: "Gneiss" },
              { value: "Phyllite", color: "#5588aa", label: "Phyllite" },
              { value: "Mica schist", color: "#336688", label: "Mica schist" },
              { value: "Shale", color: "#444444", label: "Shale" },
              { value: "Clay-bearing shale", color: "#aa6622", label: "Clay-bearing shale" },
              { value: "Fault gouge", color: "#cc3300", label: "Fault gouge" },
              { value: "Weakness zone", color: "#dd0000", label: "Weakness zone" },
            ],
          },
          { name: "q_value", type: "continuous", colorRamp: "red-green" },
        ],
      },
    ],
  },
  "mwd-demo": {
    id: "mwd-demo",
    name: "MWD Demo",
    centerlineEndpoint: "/api/mwd-demo-centerline",
    pointcloudEndpoints: [],
    pointcloudCenterlineEndpoint: "/api/mwd-demo-centerline",
    profileAssignments: [
      { fromStation: 0, toStation: 9999, profileType: "2-lane" },
    ],
    mwdEndpoint: "/api/mwd-demo-data",
    dataSummary: [
      { label: "Tunnel centerline", value: "1 alignment (demo)", available: true },
      { label: "MWD data", value: "80 rounds", available: true },
      { label: "Rock types", value: "5 classes", available: true },
      { label: "Tunnel length", value: "~80 m", available: true },
      { label: "Pointcloud scans", value: "Not available", available: false },
      { label: "Cross sections", value: "Not available", available: false },
    ],
    dataTableSchemas: [
      {
        id: "mwd_interp",
        name: "MWD Interpretations",
        endpointPattern: "/api/mwd-demo-data",
        columns: [
          {
            name: "rock_type",
            type: "categorical",
            categories: [
              { value: "good_rock", color: "#22cc44", label: "Good rock" },
              { value: "fair_rock", color: "#88bb33", label: "Fair rock" },
              { value: "poor_rock", color: "#cccc00", label: "Poor rock" },
              { value: "very_poor_rock", color: "#ee6622", label: "Very poor rock" },
              { value: "weakness_zone", color: "#dd2222", label: "Weakness zone" },
            ],
          },
        ],
      },
    ],
  },
};

export const PROJECT_LIST = Object.values(PROJECTS);

export function getProject(id: string): ProjectConfig {
  return PROJECTS[id] ?? PROJECTS["e16-fas"];
}

/** Generate a localStorage key scoped to a project */
export function storageKey(projectId: string, suffix: string): string {
  return `${projectId}:${suffix}`;
}
